import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import type { Database } from '@/integrations/supabase/types';
import {
  paymentFormMatchesPricingDisplay,
  type PricingDisplayContract,
} from '@/lib/billing/pricingDisplay';
import type { MoyasarPaymentLifecycleState } from '@/lib/billing/moyasarPaymentLifecycle';
import {
  MOYASAR_PROVIDER_METADATA_KEYS,
  type MoyasarProviderMetadata,
} from '../../../supabase/functions/_shared/moyasarProviderMetadata';

export type { MoyasarProviderMetadata };

const MOYASAR_CSS_ID = 'moyasar-payment-form-css';
const MOYASAR_SCRIPT_ID = 'moyasar-payment-form-js';
const MOYASAR_CSS_URL = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.css';
const MOYASAR_SCRIPT_URL = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.js';

export interface MoyasarPaymentFormConfig {
  provider: 'moyasar';
  transaction_id: string;
  amount: number;
  currency: string;
  description: string;
  publishable_api_key: string;
  callback_url: string;
  methods?: string[];
  supported_networks?: string[];
  credit_card?: { save_card: true };
  metadata: MoyasarProviderMetadata;
  pricing_display: PricingDisplayContract;
}

interface MoyasarPayment {
  id?: string;
  status?: string;
  verification?: Record<string, unknown>;
  [key: string]: unknown;
}

interface MoyasarWindow {
  init: (config: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    Moyasar?: MoyasarWindow;
  }
}

interface Props {
  config: MoyasarPaymentFormConfig;
  client: SupabaseClient<Database>;
  className?: string;
  onCompleted?: (payment: MoyasarPayment) => Promise<void> | void;
  onLifecycleChange?: (state: MoyasarPaymentLifecycleState) => void;
}

function hasExactMoyasarProviderBinding(config: MoyasarPaymentFormConfig): boolean {
  const metadata = config.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return false;
  const keys = Object.keys(metadata);
  return keys.length === MOYASAR_PROVIDER_METADATA_KEYS.length &&
    MOYASAR_PROVIDER_METADATA_KEYS.every((key) =>
      typeof metadata[key] === 'string' && metadata[key].trim().length > 0
    ) && metadata.transaction_id === config.transaction_id &&
    (metadata.owner_type === 'organization' || metadata.owner_type === 'candidate') &&
    ['subscription', 'credit_pack', 'add_on', 'upgrade'].includes(metadata.payment_type);
}

function ensureMoyasarAssets() {
  if (!document.getElementById(MOYASAR_CSS_ID)) {
    const link = document.createElement('link');
    link.id = MOYASAR_CSS_ID;
    link.rel = 'stylesheet';
    link.href = MOYASAR_CSS_URL;
    document.head.appendChild(link);
  }

  if (window.Moyasar) return Promise.resolve();

  const existingScript = document.getElementById(MOYASAR_SCRIPT_ID) as HTMLScriptElement | null;
  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('moyasar_script_load_failed')), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = MOYASAR_SCRIPT_ID;
    script.src = MOYASAR_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('moyasar_script_load_failed'));
    document.body.appendChild(script);
  });
}

export function MoyasarPaymentForm({
  config,
  client,
  className,
  onCompleted,
  onLifecycleChange,
}: Props) {
  const reactId = useId();
  const elementClass = useMemo(
    () => `moyasar-form-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`,
    [reactId],
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'mounting' | 'error'>('loading');
  const pricingMatches = paymentFormMatchesPricingDisplay(
    config.pricing_display,
    config.amount,
  );
  const providerBindingMatches = hasExactMoyasarProviderBinding(config);

  useEffect(() => {
    let cancelled = false;
    if (!pricingMatches || !providerBindingMatches) {
      console.error(pricingMatches
        ? '[moyasar] authoritative provider transaction binding is invalid'
        : '[moyasar] authoritative pricing does not match form amount');
      setStatus('error');
      onLifecycleChange?.('completed_or_failed');
      return () => {
        cancelled = true;
      };
    }
    setStatus('loading');

    ensureMoyasarAssets()
      .then(() => {
        if (cancelled) return;
        setStatus('mounting');
      })
      .catch((error) => {
        console.error('[moyasar] form assets failed to load', error);
        if (!cancelled) {
          setStatus('error');
          onLifecycleChange?.('completed_or_failed');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [config, onLifecycleChange, pricingMatches, providerBindingMatches]);

  useEffect(() => {
    if (status !== 'mounting') return;

    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    try {
      if (!window.Moyasar) throw new Error('moyasar_unavailable');

      container.innerHTML = '';
      container.className = `mysr-form ${elementClass}`;

      window.Moyasar.init({
        element: `.${elementClass}`,
        amount: config.amount,
        currency: config.currency,
        description: config.description,
        publishable_api_key: config.publishable_api_key,
        callback_url: config.callback_url,
        methods: config.methods ?? ['creditcard'],
        supported_networks: config.supported_networks ?? ['visa', 'mastercard', 'mada'],
        ...(config.credit_card ? { credit_card: config.credit_card } : {}),
        metadata: config.metadata,
        // Moyasar permits these fields to be refreshed immediately before it
        // creates the provider payment. Reasserting them here prevents a stale
        // or partially initialized form instance from dropping the immutable
        // Fork transaction binding during payment-method submission.
        on_initiating: () => {
          onLifecycleChange?.('initiating');
          return {
            callback_url: config.callback_url,
            metadata: config.metadata,
          };
        },
        on_completed: async (payment: MoyasarPayment) => {
          const gatewayPaymentId = payment?.id;
          if (gatewayPaymentId) {
            onLifecycleChange?.('provider_created');
            try {
              const { data, error } = await client.functions.invoke('verify-payment', {
                body: {
                  transaction_id: config.transaction_id,
                  gateway_payment_id: gatewayPaymentId,
                },
              });
              if (error) throw error;
              const verification = data as Record<string, unknown>;
              payment.verification = verification;
              if (verification.gateway_linked !== true) {
                throw new Error('gateway_payment_not_durably_linked');
              }
              const verifiedStatus = String(verification.status ?? payment.status ?? 'pending');
              if (!['pending', 'provider_pending'].includes(verifiedStatus)) {
                onLifecycleChange?.('completed_or_failed');
              }
            } catch (error) {
              console.error('[moyasar] pre-redirect verification failed', error);
            }
          } else {
            onLifecycleChange?.('completed_or_failed');
          }
          await onCompleted?.(payment);
        },
        on_failure: async () => {
          onLifecycleChange?.('completed_or_failed');
        },
      });
      onLifecycleChange?.('form_ready');

      const blankCheck = window.setTimeout(() => {
        if (!cancelled && container.childElementCount === 0) {
          console.error('[moyasar] form initialization produced an empty container');
          setStatus('error');
        }
      }, 4000);

      return () => {
        cancelled = true;
        window.clearTimeout(blankCheck);
        container.innerHTML = '';
      };
    } catch (error) {
      console.error('[moyasar] form initialization failed', error);
      setStatus('error');
      onLifecycleChange?.('completed_or_failed');
    }
  }, [client, config, elementClass, onCompleted, onLifecycleChange, status]);

  return (
    <div className={className}>
      {status === 'loading' && (
        <Card className="flex items-center gap-3 rounded-lg border-dashed p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading payment details...</span>
        </Card>
      )}
      {status === 'error' && (
        <Card className="flex items-start gap-3 rounded-lg border-destructive/30 p-4 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Payment details could not be loaded. Refresh and try again.</span>
        </Card>
      )}
      <div
        ref={containerRef}
        className={status === 'error' ? 'hidden' : `mysr-form ${elementClass}`}
      />
    </div>
  );
}
